import type { MutableRefObject } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { console } from '@splunk/dashboard-utils';
import { usePassiveEventsCheck } from './usePassiveEventsCheck';

type NoopType = (...args: never[]) => unknown;
type ListenerFactory = () => EventListenerOrEventListenerObject | undefined;

export type UseEventListenerArgs<T = Element> = {
    eventName: string;
    listener?: ListenerFactory | EventListenerOrEventListenerObject;
    target?: T | MutableRefObject<T>;
    options?: AddEventListenerOptions | boolean;
};

/**
 * Generic type guard for function returning function or function returning non-function
 * @param func Possibly-undefined function or function factory
 * @returns true if argument is a factory generating a function result
 */
const isFunctionFactory = <T>(
    func: T | (() => T | undefined) | undefined
): func is () => T => {
    if (typeof func !== 'function') {
        return false;
    }

    try {
        if (typeof (func as NoopType)() === 'function') {
            return true;
        }
    } catch {
        // Ignore the error
    }

    return false;
};

/**
 * Generic type guard for checking if argument is a ref
 * @param target Mutable ref or raw value
 * @returns true if the argument is a MutableRefObject<T> instance
 */
const isRefObject = <T>(
    target: T | MutableRefObject<T> | undefined
): target is MutableRefObject<T> => {
    const castTarget = target as MutableRefObject<NonNullable<T>>;
    const objKeys = Object.keys(castTarget ?? {});

    return objKeys.length === 1 && objKeys[0] === 'current';
};

/**
 * Allow compatibility with legacy EventListenerObject interface by returning just handleEvent function
 * @param listenerOrObject Instance of EventListener or legacy EventListenerObject
 * @returns EventListener if provided, or EventListenerObject.handleEvent function
 */
const eventListenerObjectToFunc = (
    listenerOrObject?: EventListenerOrEventListenerObject
): EventListener | undefined =>
    typeof listenerOrObject !== 'function'
        ? listenerOrObject?.handleEvent
        : listenerOrObject;

const parseListenerOptions = (
    serializedOptions: string | undefined
): AddEventListenerOptions | boolean | undefined => {
    if (!serializedOptions || serializedOptions === 'null') {
        return undefined;
    }

    try {
        return JSON.parse(serializedOptions) ?? undefined;
    } catch {
        return undefined;
    }
};

type AttachListenerConfig = {
    eventTarget: EventTarget;
    eventName: string;
    eventHandler: EventListener;
    options: AddEventListenerOptions | boolean | undefined;
    passiveEventsSupported: () => boolean;
};

const attachListener = ({
    eventTarget,
    eventName,
    eventHandler,
    options,
    passiveEventsSupported,
}: AttachListenerConfig) => {
    const addWithoutOptions = () =>
        eventTarget.addEventListener(eventName, eventHandler);

    if (typeof options === 'boolean') {
        eventTarget.addEventListener(eventName, eventHandler, options);
        return;
    }

    if (options === undefined) {
        addWithoutOptions();
        return;
    }

    if (!passiveEventsSupported()) {
        console.warn(
            'Attempted to create event listener with unsupported options'
        );
        addWithoutOptions();
        return;
    }

    eventTarget.addEventListener(eventName, eventHandler, options);
};

/**
 * Handle creation and cleanup of event listeners. If target or listener function are not provided, no handler is attached.
 * If options are provided but the client does not support passive event handlers then the options will be dropped.
 * @param {string} args.eventName Name of the event to be listened
 * @param {EventListenerOrEventListenerObject | () => (EventListenerOrEventListenerObject | undefined) | undefined} [args.listener]
 * Event handler function or event handler function factory to be run at the time of execution
 * @param {T | MutableRefObject<T> | undefined} [args.target] Target object to attach the event handler.
 * @param {AddEventListenerOptions | boolean | undefined} [args.options] Event handler options
 */
export const useEventListener = <
    T extends EventTarget | null | undefined = Element,
>({
    eventName,
    listener,
    target: targetProp,
    options: optionsProp,
}: UseEventListenerArgs<T>): void => {
    // Checker for passive event support
    const passiveEventsSupported = usePassiveEventsCheck();

    // Handle internal memoization of options to prevent new, idential
    // objects from causing detaching/reattaching of the event listener
    const memoizedOptions = useMemo(
        () => JSON.stringify(optionsProp),
        [optionsProp]
    );

    // Track the callback function and reevaluate when changed
    const memoizedListener = useMemo<EventListener | undefined>(
        () =>
            eventListenerObjectToFunc(
                isFunctionFactory<EventListenerOrEventListenerObject>(listener)
                    ? listener()
                    : listener
            ),
        [listener]
    );

    // Assign the current memoized callback to a ref to prevent reevaluation of the callback
    const eventHandlerRef = useRef<EventListener | undefined>();

    // Create a wrapping event handler as a memoized callback to prevent churn
    const eventHandler = useCallback((...args: Parameters<EventListener>) => {
        eventHandlerRef.current?.(...args);
    }, []);

    const target: NonNullable<T> | undefined = isRefObject<T>(targetProp)
        ? (targetProp.current ?? undefined)
        : (targetProp ?? undefined);

    // Track the memoized listener every time this hook runs
    eventHandlerRef.current = memoizedListener;

    // Attach/remove listener
    useLayoutEffect(() => {
        const eventTarget = target;

        if (!eventTarget) {
            return undefined;
        }

        if (typeof eventTarget.addEventListener !== 'function') {
            throw new Error(
                'Provided target does not support adding event listeners'
            );
        }

        const options = parseListenerOptions(memoizedOptions);
        attachListener({
            eventTarget,
            eventName,
            eventHandler,
            options,
            passiveEventsSupported,
        });

        // Cleanup
        return () => {
            eventTarget.removeEventListener(eventName, eventHandler);
        };
    }, [
        eventName,
        target,
        memoizedOptions,
        eventHandler,
        passiveEventsSupported,
    ]);
};
